import type { LineItemRead, LineItemScope } from '../../../rtk/rtk.generated';
import type { AppDispatch, RootState } from '../../types';
import { OptimisticUpdater } from '../../utils';

// Local type alias for the survey API instance — avoids collision with the
// `SurveyApi` types namespace already exported from survey-api/src/index.ts.
// `invalidateTags` returns a UnknownAction-shaped thunk; dispatching it
// triggers a refetch of subscribers to the given tag.
type InvalidateTagsAction = Parameters<AppDispatch>[0];
// The RTK-generated invalidateTags signature uses a mutable array of
// TagDescription unions, but we always pass a small literal so a permissive
// shape avoids importing RTK internals here.
type SurveyApiInstance = {
  util: {
    updateQueryData: (...args: never[]) => unknown;
    upsertQueryData: (...args: never[]) => unknown;
    invalidateTags: (tags: never) => InvalidateTagsAction;
  };
};

interface SetOrdinalArgs {
  lineItemId: string;
  setOrdinalRequest: { ordinal?: number };
}

interface SetOrdinalContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: unknown }>;
  getState: () => RootState;
}

/**
 * ### reorderLineItems
 *
 * @description
 * Computes a new client-side ordering that mirrors the backend
 * LineItemStorage.SetOrdinal logic: insert before the first item where
 * ordinal >= targetOrdinal, then dense-renumber to 10, 20, 30, ...
 *
 * @param items - Current ordered list of line items
 * @param movedId - The id of the item being moved
 * @param targetOrdinal - The ordinal value to insert at
 * @returns A new array with updated ordinal values
 */
const reorderLineItems = (
  items: LineItemRead[],
  movedId: string,
  targetOrdinal: number,
): LineItemRead[] => {
  const reordered = [...items];
  const sourceIndex = reordered.findIndex((li) => li.id === movedId);
  if (sourceIndex < 0) return reordered;

  const [moved] = reordered.splice(sourceIndex, 1) as [LineItemRead];
  const insertIdx = reordered.findIndex((li) => (li.ordinal ?? 0) >= targetOrdinal);
  reordered.splice(insertIdx === -1 ? reordered.length : insertIdx, 0, moved);

  // Preserve object identity for items whose ordinal does not change, so a drag
  // of one row doesn't hand every other row a fresh reference. LineItemEditDrawer
  // looks items up by id and its downstream memos compare by reference, so stable
  // references keep an open edit drawer and the list from re-rendering needlessly.
  return reordered.map((li, idx) => {
    const newOrdinal = (idx + 1) * 10;
    return li.ordinal === newOrdinal ? li : { ...li, ordinal: newOrdinal };
  });
};

/**
 * ### postLineItemsByLineItemIdSetordinalEnhancer
 *
 * @description
 * Pattern C optimistic enhancer for the postLineItemsByLineItemIdSetordinal endpoint.
 * Applies a client-side reorder to the getResultContextsByResultContextIdLineitems
 * cache before the server responds. If the request fails, the cache is rolled back.
 *
 * Sets invalidatesTags to () => [] so the auto-refetch triggered by the generated
 * 'LineItems' tag does not clobber the optimistic write — the cache already reflects
 * the new order once the mutation succeeds.
 *
 * @param api - The SurveyApi instance
 * @returns Endpoint enhancer configuration
 */
const postLineItemsByLineItemIdSetordinalEnhancer = (api: SurveyApiInstance) => ({
  // Disable tag invalidation — the optimistic update already reflects the new order.
  // The generated code has invalidatesTags: ['LineItems'] which would cause a GET
  // refetch and override our optimistic write before the response arrives.
  invalidatesTags: () => [],
  async onQueryStarted(args: SetOrdinalArgs, context: SetOrdinalContext) {
    const { lineItemId, setOrdinalRequest } = args;
    const { dispatch, queryFulfilled, getState } = context;
    const targetOrdinal = setOrdinalRequest.ordinal;

    // Silence unhandled rejection for every code path up front. Future early
    // returns don't need to remember to re-add this — the success path
    // attaches its own .then/.catch via the `await` below.
    void queryFulfilled.catch(() => {});

    if (targetOrdinal == null) return;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Find the resultContextId by searching cached lineitems queries
    const queries = getState().surveyApi?.queries;
    if (!queries) return;

    // Search all cached queries for the one holding this lineItemId. The cache
    // key now includes `scope`, so capture it alongside resultContextId and pass
    // both back to updateQueryData — otherwise the optimistic patch would target
    // the wrong (scope-less) cache entry and never reach the list being viewed.
    let resultContextId: string | undefined;
    let scope: LineItemScope | undefined;
    for (const [, query] of Object.entries(queries)) {
      const cachedQuery = query as {
        endpointName?: string;
        originalArgs?: { resultContextId?: string; scope?: LineItemScope };
        data?: LineItemRead[];
      };

      if (
        cachedQuery?.endpointName === 'getResultContextsByResultContextIdLineitems' &&
        Array.isArray(cachedQuery.data)
      ) {
        const found = cachedQuery.data.find((li) => li.id === lineItemId);
        if (found) {
          resultContextId = cachedQuery.originalArgs?.resultContextId;
          scope = cachedQuery.originalArgs?.scope;
          break;
        }
      }
    }

    if (!resultContextId) return;

    updater.update({
      endpointName: 'getResultContextsByResultContextIdLineitems',
      args: { resultContextId, scope },
      updateFn: (draft: LineItemRead[]) => {
        const reordered = reorderLineItems(draft, lineItemId, targetOrdinal);
        draft.splice(0, draft.length, ...reordered);
      },
    });

    try {
      await queryFulfilled;
    } catch {
      // Rollback all optimistic updates on failure
      updater.rollback();
      // Force a refetch to resync with server truth. The enhancer sets
      // invalidatesTags: () => [] to prevent the success-path refetch from
      // clobbering the optimistic write, but on failure we need to reconcile —
      // particularly when concurrent reorders may have left the cache in a
      // state where rolling back only this patch produces a wrong ordering.
      // Must stay in sync with TAG_TYPES.LINE_ITEM in rtk/constants.ts.
      dispatch(api.util.invalidateTags(['LineItems'] as never));
    }
  },
});

export { reorderLineItems };
export default postLineItemsByLineItemIdSetordinalEnhancer;
